/**
 * POS · Stations — the POS settings page (Owner-gated server-side). List +
 * create stations, and bring up each station's customer-facing display
 * (a station-scoped read-only link/QR to open on a second screen).
 *
 * FUNCTIONAL scaffold for the design pass — plain AntD, real API calls.
 */

import { useCallback, useEffect, useState } from "react";

import {
  App as AntApp,
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  QRCode,
  Space,
  Table,
  Typography,
} from "antd";

import {
  ApiError,
  api,
  type DisplayTokenResponse,
  type PosStation,
} from "../../api/client";

const eur = (c: number | null) =>
  c == null ? "—" : `€${(c / 100).toFixed(2)}`;

export const PosStationsPage = () => {
  const { message } = AntApp.useApp();
  const [stations, setStations] = useState<PosStation[] | null>(null);
  const [creating, setCreating] = useState(false);
  const [display, setDisplay] = useState<DisplayTokenResponse | null>(null);
  const [form] = Form.useForm<{ name: string; float?: number }>();

  const load = useCallback(async () => {
    try {
      setStations(await api.posListStations());
    } catch (e) {
      message.error(e instanceof ApiError ? e.detail : "Failed to load stations");
    }
  }, [message]);

  useEffect(() => {
    void load();
  }, [load]);

  const create = useCallback(async () => {
    const v = await form.validateFields();
    setCreating(true);
    try {
      await api.posCreateStation({
        name: v.name,
        default_opening_float_cents:
          v.float != null ? Math.round(v.float * 100) : null,
      });
      form.resetFields();
      await load();
      message.success("Station created");
    } catch (e) {
      message.error(e instanceof ApiError ? e.detail : "Create failed");
    } finally {
      setCreating(false);
    }
  }, [form, load, message]);

  const openDisplay = useCallback(
    async (s: PosStation) => {
      try {
        setDisplay(await api.posDisplayToken(s.id));
      } catch (e) {
        message.error(e instanceof ApiError ? e.detail : "Could not issue token");
      }
    },
    [message],
  );

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Typography.Title level={3}>POS stations</Typography.Title>

      <Card title="New station">
        <Form form={form} layout="inline" onFinish={create}>
          <Form.Item name="name" rules={[{ required: true }]}>
            <Input placeholder="Station name (e.g. Bar 1)" />
          </Form.Item>
          <Form.Item name="float" label="Opening float €">
            <InputNumber min={0} step={1} />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={creating}>
            Create
          </Button>
        </Form>
      </Card>

      <Table<PosStation>
        rowKey="id"
        loading={stations === null}
        dataSource={stations ?? []}
        pagination={false}
        columns={[
          { title: "ID", dataIndex: "id", width: 70 },
          { title: "Name", dataIndex: "name" },
          {
            title: "Opening float",
            dataIndex: "default_opening_float_cents",
            render: (c: number | null) => eur(c),
          },
          {
            title: "Active",
            dataIndex: "active",
            render: (a: boolean) => (a ? "Yes" : "No"),
          },
          {
            title: "Customer display",
            render: (_, s) => (
              <Button size="small" onClick={() => void openDisplay(s)}>
                Open display…
              </Button>
            ),
          },
        ]}
      />

      <Modal
        open={display !== null}
        onCancel={() => setDisplay(null)}
        footer={null}
        title={display ? `Customer display — ${display.station_name}` : ""}
      >
        {display && (
          <Space direction="vertical" align="center" style={{ width: "100%" }}>
            <Typography.Paragraph type="secondary">
              Open this on the second screen (read-only, no login):
            </Typography.Paragraph>
            <QRCode value={`${window.location.origin}${display.path}`} size={180} />
            <Typography.Link href={display.path} target="_blank">
              {window.location.origin}
              {display.path}
            </Typography.Link>
          </Space>
        )}
      </Modal>
    </Space>
  );
};
